import { Auth } from '../types/common/httpRequest'
import { AuthService } from './authService'

export type DeviceConfigurationValue = Record<string, unknown>

export const defaultDeviceConfiguration = {
    // Ship both engines; only the explicit local value selects the browser SDK.
    receiptRenderer: 'legacy',
    receiptPrinterProfile: 'thermal-80-203',
    ageVerificationEnabled: false,
    // Device session recording is opt-in; initially scoped to the ICU flow.
    recordDeviceSession: false,
    // On-screen debug controls for ICU camera age verification are off unless
    // the device configuration explicitly enables them.
    ageVerificationDebug: false,
    // Diagnostic toasts over the normal UI; independent of the ICU test page.
    ageVerificationDebugOverlay: false,
    // Card POS payment method is hidden by default; venues that have a working
    // card terminal opt-in via device configuration.
    cardPaymentEnabled: false,
    // On-screen debug panels for the card POS flow are always off unless the
    // device configuration explicitly enables them, even when card payments
    // are enabled.
    cardPaymentDebugEnabled: false,
    // Betting and gaming services are available by default; venues can opt-out
    // of either via device configuration.
    bettingEnabled: true,
    gamingEnabled: true,
    // IPS bill payment UI is incomplete and hidden (coming soon) by default;
    // venues/environments can opt-in via device configuration for staging tests.
    ipsEnabled: false,
    // Player referral program (promo-code claim + SMS enrollment) is postponed
    // until after vendor-specific bonuses. Off by default; enable per device
    // when the feature is ready to go live.
    referralEnabled: false,
    // Vendor-specific deposit bonuses are not live yet. Off by default so the
    // upcoming implementation can ship behind this flag.
    vendorBonusEnabled: false
} as const

export interface DeviceCheckInResponse {
    device: {
        deviceId: number
        agentId?: string
        statusId?: string
        typeId?: string
        name?: string
        venueId?: number
    }
    deviceConfiguration?: {
        id?: number
        name?: string
        description?: string
    }
    configurationVersion: string
    configurationUpdatedAt: string
    configuration: DeviceConfigurationValue
    jobs?: DeviceJob[]
}

export interface DeviceJob {
    deviceJobId: number
    jobType: string
    input: Record<string, unknown>
    attempts: number
    createdAt: string
}

export type DeviceCheckInResult =
    | { status: 'updated'; configuration: DeviceCheckInResponse }
    | { status: 'unchanged' }

export class DeviceCheckInMissingTokenError extends Error {
    constructor() {
        super('Device token is not available for check-in')
        this.name = 'DeviceCheckInMissingTokenError'
    }
}

export function getReceiptRendererMode(
    deviceConfiguration?: DeviceCheckInResponse | null
): 'legacy' | 'local' {
    return deviceConfiguration?.configuration?.receiptRenderer === 'local'
        ? 'local'
        : 'legacy'
}

export function getReceiptPrinterProfileId(
    deviceConfiguration?: DeviceCheckInResponse | null
): string {
    const value = deviceConfiguration?.configuration?.receiptPrinterProfile
    return typeof value === 'string' && value.trim()
        ? value.trim()
        : defaultDeviceConfiguration.receiptPrinterProfile
}

export const RECEIPT_TEMPLATE_NAMES = [
    'terminal_voucher',
    'pos_slip',
    'ips_confirmation'
] as const

export function getReceiptTemplateId(
    templateName: string,
    deviceConfiguration?: DeviceCheckInResponse | null
): string {
    const mapping = deviceConfiguration?.configuration?.receiptTemplateIds
    const value =
        mapping && typeof mapping === 'object' && !Array.isArray(mapping)
            ? (mapping as Record<string, unknown>)[templateName]
            : undefined
    const templateId =
        typeof value === 'string' && value.trim() ? value.trim() : templateName
    // Registry IDs use dashes; legacy URL rendering keeps the original name.
    return templateId.replace(/_/g, '-')
}

export function getAgeVerificationEnabled(
    deviceConfiguration?: DeviceCheckInResponse | null
): boolean {
    const value = deviceConfiguration?.configuration?.ageVerificationEnabled
    return typeof value === 'boolean'
        ? value
        : defaultDeviceConfiguration.ageVerificationEnabled
}

export function getRecordDeviceSession(
    deviceConfiguration?: DeviceCheckInResponse | null
): boolean {
    const value = deviceConfiguration?.configuration?.recordDeviceSession
    return typeof value === 'boolean'
        ? value
        : defaultDeviceConfiguration.recordDeviceSession
}

export function getAgeVerificationDebugEnabled(
    deviceConfiguration?: DeviceCheckInResponse | null
): boolean {
    const value = deviceConfiguration?.configuration?.ageVerificationDebug
    return typeof value === 'boolean'
        ? value
        : defaultDeviceConfiguration.ageVerificationDebug
}

export function getAgeVerificationDebugOverlayEnabled(
    deviceConfiguration?: DeviceCheckInResponse | null
): boolean {
    const value =
        deviceConfiguration?.configuration?.ageVerificationDebugOverlay
    return typeof value === 'boolean'
        ? value
        : defaultDeviceConfiguration.ageVerificationDebugOverlay
}

export function getCardPaymentEnabled(
    deviceConfiguration?: DeviceCheckInResponse | null
): boolean {
    const value = deviceConfiguration?.configuration?.cardPaymentEnabled
    return typeof value === 'boolean'
        ? value
        : defaultDeviceConfiguration.cardPaymentEnabled
}

export function getCardPaymentDebugEnabled(
    deviceConfiguration?: DeviceCheckInResponse | null
): boolean {
    const value = deviceConfiguration?.configuration?.cardPaymentDebugEnabled
    return typeof value === 'boolean'
        ? value
        : defaultDeviceConfiguration.cardPaymentDebugEnabled
}

export function getBettingEnabled(
    deviceConfiguration?: DeviceCheckInResponse | null
): boolean {
    const value = deviceConfiguration?.configuration?.bettingEnabled
    return typeof value === 'boolean'
        ? value
        : defaultDeviceConfiguration.bettingEnabled
}

export function getGamingEnabled(
    deviceConfiguration?: DeviceCheckInResponse | null
): boolean {
    const value = deviceConfiguration?.configuration?.gamingEnabled
    return typeof value === 'boolean'
        ? value
        : defaultDeviceConfiguration.gamingEnabled
}

export function getIpsEnabled(
    deviceConfiguration?: DeviceCheckInResponse | null
): boolean {
    const value = deviceConfiguration?.configuration?.ipsEnabled
    return typeof value === 'boolean'
        ? value
        : defaultDeviceConfiguration.ipsEnabled
}

export function getReferralEnabled(
    deviceConfiguration?: DeviceCheckInResponse | null
): boolean {
    const value = deviceConfiguration?.configuration?.referralEnabled
    return typeof value === 'boolean'
        ? value
        : defaultDeviceConfiguration.referralEnabled
}

export function getVendorBonusEnabled(
    deviceConfiguration?: DeviceCheckInResponse | null
): boolean {
    const value = deviceConfiguration?.configuration?.vendorBonusEnabled
    return typeof value === 'boolean'
        ? value
        : defaultDeviceConfiguration.vendorBonusEnabled
}

class DeviceConfigurationService {
    private currentConfiguration: DeviceCheckInResponse | null = null
    private baseUrl = import.meta.env.VITE_API_URL ?? ''

    getConfiguration(): DeviceCheckInResponse | null {
        return this.currentConfiguration
    }

    getConfigurationValue(): DeviceConfigurationValue | null {
        return this.currentConfiguration?.configuration ?? null
    }

    getConfigurationVersion(): string | null {
        return this.currentConfiguration?.configurationVersion ?? null
    }

    async checkIn(signal?: AbortSignal): Promise<DeviceCheckInResult> {
        const token = AuthService.GetToken(Auth.POS)
        if (!token) {
            throw new DeviceCheckInMissingTokenError()
        }

        const configurationVersion = this.getConfigurationVersion()
        const query = configurationVersion
            ? `?configurationVersion=${encodeURIComponent(configurationVersion)}`
            : ''

        const response = await fetch(
            `${this.baseUrl}/auth/device/check-in${query}`,
            {
                method: 'GET',
                signal,
                headers: {
                    Authorization: token,
                    'Cache-Control': 'no-cache',
                    'Content-Language': 'sr-SP'
                }
            }
        )

        if (response.status === 204) {
            return { status: 'unchanged' }
        }

        if (!response.ok) {
            throw new Error(`Device check-in failed: ${response.status}`)
        }

        const configuration = (await response.json()) as DeviceCheckInResponse
        this.currentConfiguration = configuration

        return {
            status: 'updated',
            configuration
        }
    }

    async reportJobSuccess(deviceJobId: number): Promise<void> {
        await this.reportJobResult(deviceJobId, 'success')
    }

    async reportJobFailure(deviceJobId: number, error: string): Promise<void> {
        await this.reportJobResult(deviceJobId, 'failure', { error })
    }

    private async reportJobResult(
        deviceJobId: number,
        result: 'success' | 'failure',
        body?: Record<string, unknown>
    ): Promise<void> {
        const token = AuthService.GetToken(Auth.POS)
        if (!token) {
            throw new DeviceCheckInMissingTokenError()
        }

        const response = await fetch(
            `${this.baseUrl}/auth/device/jobs/${deviceJobId}/${result}`,
            {
                method: 'POST',
                headers: {
                    Authorization: token,
                    'Content-Type': 'application/json',
                    'Content-Language': 'sr-SP'
                },
                body: body ? JSON.stringify(body) : undefined
            }
        )

        if (!response.ok) {
            throw new Error(
                `Device job ${result} report failed: ${response.status}`
            )
        }
    }
}

export const deviceConfigurationService = new DeviceConfigurationService()
